"use client"

import { useSearchParams } from "next/navigation"
import { useEffect, useState, useTransition } from "react"
import { useInView } from "react-intersection-observer"
import { HttpTypes } from "@medusajs/types"
import ProductPreviewClient from "@modules/products/components/product-preview/client"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { loadMoreProducts } from "@lib/actions/products"

type SortableProductListProps = {
  initialProducts: HttpTypes.StoreProduct[]
  count: number
  categoryIds?: string[]
  collectionId?: string
  productsIds?: string[]
  countryCode: string
  limit?: number
  parentCategoryHandle?: string
  siblingCategoryHandles?: string[]
}

export default function SortableProductList({
  initialProducts,
  count,
  categoryIds,
  collectionId,
  productsIds,
  countryCode,
  limit = 12,
  parentCategoryHandle,
  siblingCategoryHandles,
}: SortableProductListProps) {
  const searchParams = useSearchParams()
  const sortBy = (searchParams.get("sortBy") as SortOptions) || "created_at"

  const [products, setProducts] = useState(initialProducts)
  const [totalCount, setTotalCount] = useState(count)
  const [hasMore, setHasMore] = useState(initialProducts.length < count)
  const [isPending, startTransition] = useTransition()
  const [currentSort, setCurrentSort] = useState<SortOptions>("created_at")
  const { ref, inView } = useInView({ threshold: 0 })

  // Infinite scroll trigger
  useEffect(() => {
    if (inView && hasMore && !isPending) {
      handleLoadMore()
    }
  }, [inView, hasMore, isPending]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when sort changes
  useEffect(() => {
    if (sortBy !== currentSort) {
      setCurrentSort(sortBy)
      startTransition(async () => {
        const result = await loadMoreProducts({
          countryCode,
          offset: 0,
          limit,
          sortBy,
          categoryIds,
          collectionId,
          productsIds,
          parentCategoryHandle,
          siblingCategoryHandles,
        })
        setProducts(result.products)
        setTotalCount(result.count)
        setHasMore(result.hasMore)
      })
    }
  }, [sortBy]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    startTransition(async () => {
      const result = await loadMoreProducts({
        countryCode,
        offset: products.length,
        limit,
        sortBy,
        categoryIds,
        collectionId,
        productsIds,
        parentCategoryHandle,
        siblingCategoryHandles,
      })

      setProducts((prev) => [...prev, ...result.products])
      setTotalCount(result.count)
      setHasMore(result.hasMore)
    })
  }

  return (
    <>
      <ul
        className="grid grid-cols-2 w-full small:grid-cols-3 medium:grid-cols-4 gap-x-6 gap-y-8"
        data-testid="products-list"
      >
        {products.map((p) => (
          <li key={p.id}>
            <ProductPreviewClient product={p} />
          </li>
        ))}
      </ul>

      <div className="flex flex-col items-center gap-4 mt-8">
        <p className="text-ui-fg-muted text-small-regular" data-testid="product-count">
          Showing {products.length} of {totalCount} products
        </p>
        {hasMore && (
          <div ref={ref} className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
          </div>
        )}
      </div>
    </>
  )
}
